import { TZDate } from '@date-fns/tz'

/**
 * Convert a UTC API date string into a plain Date that Mantine's
 * DateTimePicker will display as the correct wall-clock time.
 *
 * e.g. "2024-02-26T16:00:00Z" + "America/Chicago" → Date showing 10:00
 */
export const utcToMantineDate = (
  utcDate?: string | null,
  timezone?: string,
): Date | null => {
  if (!utcDate || !timezone) return null

  const tzDate = new TZDate(utcDate, timezone)

  // Construct a plain Date whose local fields match the target tz wall-clock time.
  // Mantine reads getFullYear/getMonth/etc. — so we mirror those from the TZDate.
  return new Date(
    tzDate.getFullYear(),
    tzDate.getMonth(),
    tzDate.getDate(),
    tzDate.getHours(),
    tzDate.getMinutes(),
    tzDate.getSeconds(),
  )
}

/**
 * Normalise whatever Mantine's DateTimePicker hands back into a plain Date.
 */
const toPlainDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  // Mantine sometimes returns a dayjs object
  if (typeof (value as any).toDate === 'function') return (value as any).toDate()
  // Fallback: string or number
  const d = new Date(value as any)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Convert a Date coming back out of Mantine's DateTimePicker into a UTC
 * ISO string, treating the picker value as if it were in the target timezone.
 *
 * e.g. picker shows 10:00, timezone "America/Chicago" → "2024-02-26T16:00:00.000Z"
 */
export const mantineDateToUtc = (
  pickerDate?: Date | null,
  timezone?: string,
): string | null => {
  if (!pickerDate || !timezone) return null

  const plain = toPlainDate(pickerDate)
  if (!plain) return null

  // The picker gave us a Date where the *local* fields represent the target tz time.
  // We reinterpret those fields as if they are in the target tz to get the true UTC moment.
  const tzDate = new TZDate(
    plain.getFullYear(),
    plain.getMonth(),
    plain.getDate(),
    plain.getHours(),
    plain.getMinutes(),
    plain.getSeconds(),
    timezone,
  )

  return tzDate.toISOString() // Back to UTC for the API
}